import Button from "@components/button";
import Column from "@components/column";
import Fields from "@components/fields";
import Row from "@components/row";
import Typography from "@components/typography";
import Wrapper from "@components/wrapper";
import { FieldArray, useFormikContext } from "formik";
import { Trash, Plus, Clock } from "lucide-react";
import React from "react";

import TimeSelector from "@commons/time-selector";

const TwoWheeler = ({ viewMode }: { viewMode: string }) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const isFixed = viewMode === "fixed";
  const prefix = "twoWheeler";

  const gridStyle = isFixed
    ? "display: grid; grid-template-columns: 200px 1fr 40px; gap: 20px; align-items: end;"
    : "display: grid; grid-template-columns: 200px 1fr 1fr 1fr 40px; gap: 15px; align-items: end;";

  return (
    <Wrapper stylewrapper="margin-top: 20px;">
      <Row styleRow="justify-content: space-between; align-items: center; margin-bottom: 15px;">
        <Typography variant="h6">2 Wheeler Configuration</Typography>
      </Row>

      <Column stylecolumn="gap: 20px;">
        {!isFixed && (
          <Row styleRow="gap: 20px; padding: 20px; border: 1px solid #E2E8F0; border-radius: 8px; background: #F8FAFC; align-items: flex-start;">
            <div style={{ width: "250px", flexShrink: 0 }}>
              <Fields.Input
                name={`${prefix}.salary`}
                label="Monthly Base Salary (Rs)"
                placeholder="e.g. 15000"
                type="number"
              />
            </div>

            <div
              style={{
                flex: 1,
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                padding: "20px",
                background: "#FFF",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#1E293B",
                      display: "block",
                    }}
                  >
                    Incentive Configuration
                  </label>
                  <span style={{ fontSize: "12px", color: "#64748B" }}>
                    Configure delivery incentives based on order count
                  </span>
                </div>

                <div
                  onClick={() =>
                    setFieldValue(
                      `${prefix}.hasIncentive`,
                      !values[prefix].hasIncentive,
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: values[prefix].hasIncentive
                        ? "#059669"
                        : "#64748B",
                    }}
                  >
                    {values[prefix].hasIncentive ? "Active" : "Inactive"}
                  </span>
                  <div
                    style={{
                      width: "44px",
                      height: "24px",
                      background: values[prefix].hasIncentive
                        ? "#10B981"
                        : "#E2E8F0",
                      borderRadius: "24px",
                      position: "relative",
                      transition: "all 0.2s",
                    }}
                  >
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        background: "#FFF",
                        borderRadius: "50%",
                        position: "absolute",
                        top: "2px",
                        left: values[prefix].hasIncentive ? "22px" : "2px",
                        transition: "all 0.2s",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                      }}
                    />
                  </div>
                </div>
              </div>

              <FieldArray
                name={`${prefix}.incentives`}
                render={(arrayHelpers) => (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "15px",
                    }}
                  >
                    {values[prefix]?.incentives?.map(
                      (_: any, index: number) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            gap: "12px",
                            alignItems: "flex-end",
                            opacity: values[prefix].hasIncentive ? 1 : 0.6,
                            pointerEvents: values[prefix].hasIncentive
                              ? "auto"
                              : "none",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <Fields.Input
                              name={`${prefix}.incentives.${index}.minOrder`}
                              label="Min Order"
                              placeholder="0"
                              type="number"
                            />
                          </div>
                          <div
                            style={{
                              paddingBottom: "14px",
                              color: "#64748B",
                              fontWeight: 500,
                            }}
                          >
                            to
                          </div>
                          <div style={{ flex: 1 }}>
                            <Fields.Input
                              name={`${prefix}.incentives.${index}.maxOrder`}
                              label="Max Order"
                              placeholder="0"
                              type="number"
                            />
                          </div>

                          <div style={{ flex: 1.2 }}>
                            <label
                              style={{
                                fontSize: "15px",
                                fontWeight: 600,
                                color: "#1E293B",
                                marginBottom: "6px",
                                display: "block",
                              }}
                            >
                              Type
                            </label>
                            <select
                              name={`${prefix}.incentives.${index}.type`}
                              value={values[prefix]?.incentives[index]?.type}
                              onChange={(e) =>
                                setFieldValue(
                                  `${prefix}.incentives.${index}.type`,
                                  e.target.value,
                                )
                              }
                              style={{
                                height: "40px",
                                border: "1px solid #E2E8F0",
                                borderRadius: "6px",
                                padding: "0 10px",
                                fontSize: "14px",
                                color: "#334155",
                                outline: "none",
                                background: "#FFF",
                                width: "100%",
                                cursor: "pointer",
                              }}
                            >
                              <option value="flat">Flat (Rs)</option>
                              <option value="percentage">Percentage (%)</option>
                            </select>
                          </div>

                          <div style={{ flex: 1 }}>
                            <Fields.Input
                              name={`${prefix}.incentives.${index}.amount`}
                              label="Amount"
                              placeholder="0"
                              type="number"
                            />
                          </div>

                          <div
                            onClick={() => arrayHelpers.remove(index)}
                            style={{
                              height: "38px",
                              width: "38px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              borderRadius: "6px",
                              color: "#EF4444",
                              background: "#FEF2F2",
                              border: "1px solid #FEE2E2",
                              marginBottom: "2px",
                            }}
                          >
                            <Trash size={16} />
                          </div>
                        </div>
                      ),
                    )}

                    <div
                      onClick={() =>
                        arrayHelpers.push({
                          minOrder: 0,
                          maxOrder: 0,
                          type: "flat",
                          amount: 0,
                        })
                      }
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        cursor: "pointer",
                        color: "#DC2626",
                        fontWeight: 600,
                        fontSize: "12px",
                        width: "fit-content",
                        padding: "6px 14px",
                        borderRadius: "20px",
                        background: "#FFF",
                        border: "1px dashed #DC2626",
                        alignSelf: "flex-start",
                        marginTop:
                          values[prefix]?.incentives?.length > 0
                            ? "0px"
                            : "10px",
                      }}
                    >
                      <Plus size={14} />
                      <span>Add Tier</span>
                    </div>
                  </div>
                )}
              />
            </div>
          </Row>
        )}

        <FieldArray
          name={`${prefix}.slots`}
          render={(arrayHelpers) => (
            <Column stylecolumn="gap: 0;">
              {values[prefix]?.slots?.length === 0 && (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    border: "1px dashed #E2E8F0",
                    borderRadius: "8px",
                    color: "#64748B",
                  }}
                >
                  No time slots configured. Add one below.
                </div>
              )}

              {values[prefix]?.slots?.map((_: any, index: number) => (
                <div
                  key={index}
                  style={{
                    padding: "20px 0",
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <div
                    style={{
                      ...(isFixed
                        ? {
                            display: "grid",
                            gridTemplateColumns: "1.5fr 1fr 40px",
                            gap: "20px",
                            alignItems: "end",
                          }
                        : {
                            display: "grid",
                            gridTemplateColumns: "1.5fr 1fr 1fr 1fr 40px",
                            gap: "15px",
                            alignItems: "end",
                          }),
                    }}
                  >
                    {/* Time Range Group */}
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center",
                      }}
                    >
                      <TimeSelector
                        label="FROM"
                        value={
                          values[prefix]?.slots?.[index]?.timeSlot?.split(
                            " - ",
                          )[0] || ""
                        }
                        onChange={(val) => {
                          const end =
                            values[prefix]?.slots?.[index]?.timeSlot?.split(
                              " - ",
                            )[1] || "12:00 PM";
                          setFieldValue(
                            `${prefix}.slots.${index}.timeSlot`,
                            `${val} - ${end}`,
                          );
                        }}
                      />
                      <span
                        style={{
                          paddingTop: "24px",
                          color: "#94A3B8",
                          fontSize: "12px",
                        }}
                      >
                        TO
                      </span>
                      <TimeSelector
                        label="TO"
                        value={
                          values[prefix]?.slots?.[index]?.timeSlot?.split(
                            " - ",
                          )[1] || ""
                        }
                        onChange={(val) => {
                          const start =
                            values[prefix]?.slots?.[index]?.timeSlot?.split(
                              " - ",
                            )[0] || "12:00 AM";
                          setFieldValue(
                            `${prefix}.slots.${index}.timeSlot`,
                            `${start} - ${val}`,
                          );
                        }}
                      />
                    </div>

                    {isFixed ? (
                      <Fields.Input
                        name={`${prefix}.slots.${index}.price`}
                        label="Price (Rs)"
                        placeholder="0"
                        type="number"
                      />
                    ) : (
                      <>
                        <Fields.Input
                          name={`${prefix}.slots.${index}.minCharge`}
                          label="Minimum Charge (Rs)"
                          placeholder="0"
                          type="number"
                        />
                        <Fields.Input
                          name={`${prefix}.slots.${index}.perKm`}
                          label="Per Km"
                          placeholder="0"
                          type="number"
                        />
                        <Fields.Input
                          name={`${prefix}.slots.${index}.maxCharge`}
                          label="Maximum Charge (Rs)"
                          placeholder="0"
                          type="number"
                        />
                      </>
                    )}

                    <div
                      onClick={() => arrayHelpers.remove(index)}
                      style={{
                        height: "38px", // match input height roughly
                        width: "38px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        borderRadius: "6px",
                        color: "#EF4444",
                        background: "#FEF2F2",
                        border: "1px solid #FEE2E2",
                        marginBottom: "2px", // Alignment tweak for input label offset
                      }}
                      title="Remove Slot"
                    >
                      <Trash size={16} />
                    </div>
                  </div>
                </div>
              ))}

              <div
                onClick={() =>
                  arrayHelpers.push(
                    isFixed
                      ? { timeSlot: "09:00 AM - 05:00 PM", price: 0 }
                      : {
                          timeSlot: "09:00 AM - 05:00 PM",
                          minCharge: 0,
                          perKm: 0,
                          maxCharge: 0,
                        },
                  )
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                  color: "#DC2626",
                  fontWeight: 600,
                  fontSize: "12px",
                  width: "fit-content",
                  padding: "6px 14px",
                  borderRadius: "20px",
                  background: "#FEF2F2",
                  border: "1px solid #FEE2E2",
                  transition: "all 0.2s",
                  marginTop: "10px",
                }}
              >
                <Plus size={14} />
                <span>Add Time Slot</span>
              </div>
            </Column>
          )}
        />
      </Column>
    </Wrapper>
  );
};

export default TwoWheeler;
